import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
} from 'recharts';
import { moderationService } from '../services/moderationService';

interface UserBehaviorReport {
  report_id: string;
  generated_at: string;
  period: {
    days: number;
    start_date: string;
    end_date: string;
  };
  summary: {
    total_active_users: number;
    users_with_violations: number;
    high_risk_users: number;
    repeat_offenders: number;
  };
  content_patterns: any[];
  suspicious_patterns: any[];
  risk_scores: any[];
  repeat_offenders: any[];
  engagement_metrics: any;
}

interface SuspiciousActivityAnalysis {
  user_id: string;
  analysis_period: {
    hours: number;
    start_time: string;
    end_time: string;
  };
  total_activities: number;
  suspicious_indicators: any[];
  suspicion_score: number;
  risk_level: string;
  recommended_action: string;
}

const UserBehaviorAnalytics: React.FC = () => {
  const [behaviorReport, setBehaviorReport] = useState<UserBehaviorReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [selectedUser, setSelectedUser] = useState('');
  const [userAnalysisDialog, setUserAnalysisDialog] = useState(false);
  const [userAnalysis, setUserAnalysis] = useState<SuspiciousActivityAnalysis | null>(null);
  const [analyzingUser, setAnalyzingUser] = useState(false);

  useEffect(() => {
    loadBehaviorReport();
  }, [selectedPeriod]);

  const loadBehaviorReport = async () => {
    try {
      setLoading(true);
      const data = await moderationService.getUserBehaviorAnalytics(selectedPeriod);
      setBehaviorReport(data);
      setError(null);
    } catch (err) {
      setError('Failed to load user behavior analytics');
      console.error('Error loading behavior analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const analyzeUserActivity = async () => {
    if (!selectedUser) return;

    try {
      setAnalyzingUser(true);
      const analysis = await moderationService.detectUserSuspiciousActivity(selectedUser);
      setUserAnalysis(analysis);
      setUserAnalysisDialog(true);
    } catch (err) {
      setError('Failed to analyze user activity');
      console.error('Error analyzing user:', err);
    } finally {
      setAnalyzingUser(false);
    }
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'success';
    }
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high':
        return <ErrorIcon />;
      case 'medium':
        return <WarningIcon />;
      case 'low':
        return <InfoIcon />;
      default:
        return <SecurityIcon />;
    }
  };

  const formatChartData = (data: Record<string, number>) => {
    return Object.entries(data).map(([key, value]) => ({
      name: key.replace('_', ' ').toUpperCase(),
      value,
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadBehaviorReport} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!behaviorReport) {
    return null;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          User Behavior Analytics
        </Typography>
        
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as number)}
            label="Period"
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Users
              </Typography>
              <Typography variant="h4">
                {behaviorReport.summary.total_active_users}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Users with Violations
              </Typography>
              <Typography variant="h4" color="warning.main">
                {behaviorReport.summary.users_with_violations}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                High Risk Users
              </Typography>
              <Typography variant="h4" color="error.main">
                {behaviorReport.summary.high_risk_users}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Repeat Offenders
              </Typography>
              <Typography variant="h4" color="error.main">
                {behaviorReport.summary.repeat_offenders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* User Analysis Tool */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Individual User Analysis
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  label="User ID"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  placeholder="Enter user ID to analyze"
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={analyzeUserActivity}
                  disabled={!selectedUser || analyzingUser}
                  startIcon={analyzingUser ? <CircularProgress size={20} /> : <SearchIcon />}
                >
                  {analyzingUser ? 'Analyzing...' : 'Analyze User'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* High Risk Users */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                High Risk Users
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>Risk Score</TableCell>
                      <TableCell>Risk Level</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {behaviorReport.risk_scores.slice(0, 10).map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell>{user.user_id}</TableCell>
                        <TableCell>{user.risk_score?.toFixed(1) || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getRiskLevelIcon(user.risk_level)}
                            label={user.risk_level}
                            color={getRiskLevelColor(user.risk_level) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() => {
                              setSelectedUser(user.user_id);
                              analyzeUserActivity();
                            }}
                          >
                            Analyze
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Repeat Offenders */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Repeat Offenders
              </Typography>
              <List>
                {behaviorReport.repeat_offenders.slice(0, 5).map((offender, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <PersonIcon color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={offender.user_id || `User ${index + 1}`}
                      secondary={`${offender.violation_count || 0} violations - ${offender.recommended_action || 'Review needed'}`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Suspicious Patterns */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detected Suspicious Patterns
              </Typography>
              {behaviorReport.suspicious_patterns.length === 0 ? (
                <Alert severity="success">
                  No suspicious patterns detected in the selected period.
                </Alert>
              ) : (
                behaviorReport.suspicious_patterns.map((pattern, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <WarningIcon color="warning" />
                        <Typography>
                          Pattern {index + 1}: {pattern.type || 'Unknown Pattern'}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                        {JSON.stringify(pattern, null, 2)}
                      </pre>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Analysis Dialog */}
      <Dialog
        open={userAnalysisDialog}
        onClose={() => setUserAnalysisDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Suspicious Activity Analysis: {userAnalysis?.user_id}
        </DialogTitle>
        
        <DialogContent>
          {userAnalysis && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Analysis Period:
                  </Typography>
                  <Typography variant="body1">
                    {userAnalysis.analysis_period.hours} hours
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Total Activities:
                  </Typography>
                  <Typography variant="body1">
                    {userAnalysis.total_activities}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Suspicion Score:
                  </Typography>
                  <Typography variant="h6" color={getRiskLevelColor(userAnalysis.risk_level) + '.main'}>
                    {userAnalysis.suspicion_score.toFixed(1)}/10
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Risk Level:
                  </Typography>
                  <Chip
                    icon={getRiskLevelIcon(userAnalysis.risk_level)}
                    label={userAnalysis.risk_level}
                    color={getRiskLevelColor(userAnalysis.risk_level) as any}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>
                Recommended Action: {userAnalysis.recommended_action.replace('_', ' ').toUpperCase()}
              </Typography>

              {userAnalysis.suspicious_indicators.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>
                    Suspicious Indicators:
                  </Typography>
                  {userAnalysis.suspicious_indicators.map((indicator, index) => (
                    <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                      {indicator.type || `Indicator ${index + 1}`}: {indicator.description || 'No description available'}
                    </Alert>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setUserAnalysisDialog(false)}>
            Close
          </Button>
          {userAnalysis && userAnalysis.recommended_action !== 'no_action' && (
            <Button variant="contained" color="warning">
              Take Action
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserBehaviorAnalytics;